import React, { useState, useEffect } from "react";
import { Card, Field, Badge, SectionTitle } from "@/components/ui-hud";
import AirportInput from "@/components/AirportInput";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Search as SearchIcon, MapPin, Plane, ShieldCheck, Users, Loader2, ArrowRight, Send, Briefcase, Radio, User as UserIcon, Lock } from "lucide-react";
import { toast } from "sonner";
import { Link, useNavigate, useLocation } from "react-router-dom";

const ROLES = ["Any", "PIC", "SIC"];

export default function PilotSearch() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState({ airport_icao: "", type_rating: "", role: "Any", faa_verified_only: true });
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState({});

  useEffect(() => {
    if (authLoading || !user) return;
    api.get("/faa/type-ratings/catalog").then((r) => setCatalog(r.data || {})).catch(() => {});
  }, [authLoading, user]);

  const run = async (e) => {
    e?.preventDefault?.();
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`);
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/pilots/search", q);
      setResults(r.data.results);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401) {
        toast.error("Your session expired. Please log in again to search crew.");
        localStorage.removeItem("pjp_token");
        navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`);
      } else {
        toast.error(detail || "Search failed");
      }
    } finally { setBusy(false); }
  };

  // Gate: user must be logged in to access the 26,500-member directory.
  if (authLoading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-cyan" />
      </div>
    );
  }
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return (
      <div className="max-w-3xl mx-auto px-6 py-20" data-testid="crew-search-login-gate">
        <Card>
          <div className="text-center py-10 px-4">
            <div className="mx-auto h-14 w-14 rounded-full border border-cyan/40 bg-cyan/10 flex items-center justify-center mb-5">
              <Lock className="h-6 w-6 text-cyan" />
            </div>
            <div className="overline text-cyan mb-2">// MEMBERS-ONLY · CREW DIRECTORY</div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white mb-3">
              Log in to search 26,500+ verified crew.
            </h1>
            <p className="text-white/60 max-w-lg mx-auto mb-6">
              Our global roster of FAA-verified pilots, dispatchers, cabin crew and mechanics is gated. Sign in or join PJP to search, view files and message members directly.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to={`/login?next=${next}`} data-testid="crew-search-login-btn" className="btn btn-primary">
                Log in →
              </Link>
              <Link to={`/register?next=${next}`} data-testid="crew-search-register-btn" className="btn btn-ghost">
                Join PJP (free)
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <SectionTitle
        overline="// FIND CREW · DATABASE SEARCH"
        title="Search our database of 26,500+ crew."
        subtitle="Filter our global roster by home airport, FAA type rating and PIC/SIC preference. Click any pilot to see their full file, verified documents and message them directly."
      />

      <Card className="mb-8">
        <form onSubmit={run} className="grid md:grid-cols-[1fr_1fr_180px_140px_auto] gap-3 items-end">
          <Field label="Airport (ICAO or City)">
            <AirportInput
              value={q.airport_icao}
              onChange={(v) => setQ({ ...q, airport_icao: v })}
              testId="search-airport"
              placeholder="KTEB, KOPF, Miami…"
            />
          </Field>
          <Field label="Type Rating">
            <select
              data-testid="search-type"
              className="input"
              value={q.type_rating}
              onChange={(e) => setQ({ ...q, type_rating: e.target.value })}
            >
              <option value="">Any type</option>
              {Object.entries(catalog)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([oem, list]) => (
                  <optgroup key={oem} label={oem}>
                    {[...list].sort((a, b) => a.label.localeCompare(b.label)).map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                  </optgroup>
                ))}
            </select>
          </Field>
          <Field label="Role">
            <select
              data-testid="search-role"
              className="input"
              value={q.role}
              onChange={(e) => setQ({ ...q, role: e.target.value })}
            >
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <label className="flex items-center gap-2 text-sm text-white/70 pb-3">
            <input
              type="checkbox"
              data-testid="search-verified-only"
              checked={q.faa_verified_only}
              onChange={(e) => setQ({ ...q, faa_verified_only: e.target.checked })}
              className="h-4 w-4"
            />
            FAA verified
          </label>
          <button data-testid="btn-search-pilots" disabled={busy} className="btn btn-primary justify-center h-[42px]">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><SearchIcon className="h-4 w-4" /> Search</>}
          </button>
        </form>
      </Card>

      {results !== null && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="overline text-cyan">// RESULTS · {results.length} MATCH{results.length === 1 ? "" : "ES"}</div>
            {results.length > 0 && (
              <Link to="/hire-now" data-testid="btn-cta-hire-now" className="btn btn-primary" style={{background:"#EB5757", borderColor:"#EB5757"}}>
                NEED A PILOT NOW? →
              </Link>
            )}
          </div>
          {results.length === 0 ? (
            <Card><div className="text-center py-8 text-white/60">
              No pilots matched. Try widening the type rating or dropping the FAA-verified filter.
            </div></Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
              {results.map((p) => <PilotCard key={p.id} p={p} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PilotCard({ p }) {
  const ratings = (p.aircraft_types || []).slice(0, 6);
  const isCurrent = (t) => (p.training_current || []).includes(t); // future: from training_events collection
  const displayName = p.nickname ? p.nickname : `${p.first_name} ${p.last_name}`;
  const photoUrl = p.id_photo_url || p.avatar_url;
  const location = p.location_icao || p.location || "—";

  return (
    <Link
      to={`/crew/${p.id}`}
      data-testid={`pilot-card-${p.id}`}
      className="block group relative rounded-lg border border-white/10 bg-white/[0.02] hover:border-cyan/60 hover:bg-white/[0.04] transition-all p-4 focus:outline-none focus:ring-2 focus:ring-cyan/60"
    >
      <div className="flex items-start gap-3 mb-3">
        {/* Avatar */}
        <div className="h-14 w-14 rounded-lg overflow-hidden border border-white/15 flex-shrink-0 bg-white/[0.03] flex items-center justify-center">
          {photoUrl ? (
            <img src={photoUrl} alt={displayName} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <UserIcon className="h-6 w-6 text-white/40" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="font-display text-lg font-semibold tracking-tight text-white truncate">{displayName}</div>
            {p.faa_verified && (
              <span className="flex items-center gap-1 text-[10px] mono text-avail flex-shrink-0">
                <ShieldCheck className="h-3.5 w-3.5" /> FAA
              </span>
            )}
          </div>
          <div className="mono text-[11px] text-white/50 flex items-center gap-1 mt-0.5">
            <MapPin className="h-3 w-3" /> {location}
          </div>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {p.available_to_work && (
              <span className="mono text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-avail/15 text-avail border border-avail/40 flex items-center gap-1">
                <Briefcase className="h-2.5 w-2.5" /> AVAILABLE
              </span>
            )}
            {p.share_live_location && (
              <span className="mono text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-cyan/15 text-cyan border border-cyan/40 flex items-center gap-1">
                <Radio className="h-2.5 w-2.5" /> LIVE
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 my-3">
        <div>
          <div className="eyebrow-muted mb-1">Hours</div>
          <div className="mono text-cyan">{(p.total_hours || 0).toLocaleString()}</div>
        </div>
        <div>
          <div className="eyebrow-muted mb-1">Seat</div>
          <div className="mono text-cyan">{p.role_profile?.preferred_seat || "PIC/SIC"}</div>
        </div>
        <div>
          <div className="eyebrow-muted mb-1">Ratings</div>
          <div className="mono text-cyan">{ratings.length}</div>
        </div>
      </div>
      {ratings.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-2 border-t border-white/[0.06]">
          {ratings.map((t) => (
            <span
              key={t}
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] mono font-semibold border ${isCurrent(t) ? "" : ""}`}
              style={isCurrent(t)
                ? { background: "rgba(46,213,115,0.16)", borderColor: "#2ED573", color: "#2ED573" }
                : { background: "rgba(75,156,255,0.10)", borderColor: "rgba(75,156,255,0.45)", color: "#4B9CFF" }}
              title={isCurrent(t) ? "Training current" : "Rated (training currency unknown)"}
            >
              {t}
            </span>
          ))}
        </div>
      )}
      {/* Hover reveal */}
      <div className="mt-3 pt-3 border-t border-white/[0.04] flex items-center justify-between gap-2 text-[11px] text-white/40 group-hover:text-cyan transition-colors">
        <span className="mono tracking-widest uppercase flex items-center gap-1">
          <Send className="h-3 w-3" /> View file & message
        </span>
        <ArrowRight className="h-3.5 w-3.5" />
      </div>
    </Link>
  );
}
